/**
 * What the person must see at the bottom of the frame: the app's notices, and
 * each write the server refused for good, with what it was (25B rule 9). A
 * refused write can be sent again or dismissed. A write that is only waiting
 * for signal is never shown: it shows as done, from the cache.
 *
 * A note edit refused because the note changed elsewhere first (409, 25C2)
 * offers Compare instead of Send again: both versions side by side, and the
 * person keeps one (27B, `widgets/notes/Conflict.tsx`, loaded when opened).
 */
import { lazy, Suspense, useState } from "react";

import { enqueue, dismiss, type Failure } from "../offline/outbox";
import { describe } from "./failure";
import { openOffline } from "../offline/idb";
import { requestDrain } from "../offline/register";
import { useFailures } from "../offline/useOffline";
import { useNotices } from "./notices";
import styles from "./Tray.module.css";

const Conflict = lazy(() =>
  import("../widgets/notes/Conflict").then((m) => ({ default: m.Conflict })),
);

/** A note's words, refused because another device changed them first. */
const isConflict = (f: Failure) => f.status === 409 && f.write.entity === "note";

async function withStore(run: (db: IDBDatabase) => Promise<void>): Promise<void> {
  const db = await openOffline();
  try {
    await run(db);
  } finally {
    db.close();
  }
}

async function sendAgain(failure: Failure): Promise<void> {
  const { method, path, body, entity, entityId } = failure.write;
  await withStore(async (db) => {
    await enqueue(db, { method, path, body, entity, entityId });
    await dismiss(db, failure.seq);
  });
  await requestDrain();
}

export function Tray() {
  const failures = useFailures() ?? [];
  const notices = useNotices((s) => s.notices);
  const dismissNotice = useNotices((s) => s.dismiss);
  const [comparing, setComparing] = useState<Failure | null>(null);
  if (!failures.length && !notices.length) return null;
  return (
    <div className={styles.tray} aria-live="polite">
      {notices.map((notice) => (
        <div key={`n${notice.id}`} className={styles.item}>
          <span className={styles.text}>{notice.text}</span>
          <button
            className={styles.quiet}
            onClick={() => dismissNotice(notice.id)}
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      ))}
      {failures.map((failure) => (
        <div key={`f${failure.seq}`} className={`${styles.item} ${styles.failed}`} role="alert">
          <span className={styles.text}>
            <strong>{describe(failure)} did not save.</strong> {failure.detail}
          </span>
          {isConflict(failure) ? (
            <button className={styles.action} onClick={() => setComparing(failure)}>
              Compare
            </button>
          ) : (
            <button className={styles.action} onClick={() => void sendAgain(failure)}>
              Send again
            </button>
          )}
          <button
            className={styles.quiet}
            onClick={() => void withStore((db) => dismiss(db, failure.seq))}
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      ))}
      {comparing && (
        <Suspense fallback={null}>
          <Conflict failure={comparing} onClose={() => setComparing(null)} />
        </Suspense>
      )}
    </div>
  );
}
